// Shader preset thumbs: drawn once on save and uploaded, never on read — a cold pipeline link is seconds.
import { useSyncExternalStore } from 'react'
import { THUMBNAIL_MIME, THUMBNAIL_QUALITY } from '~/lib/constants'
import { clockAt, drawShaderFrame, getShader, THUMB_TIME, thumbUniforms, VERTEX } from '~/lib/shaders'

const SIZE = 256

// presetId → blob url drawn this session, shown until a reload brings the uploaded one's.
const local = new Map()
const listeners = new Set()
let version = 0
let ctx = null
// Draws one at a time: they share the context.
let chain = Promise.resolve()

const context = () => {
	if (ctx && !ctx.gl.isContextLost()) return ctx
	const canvas = new OffscreenCanvas(SIZE, SIZE)
	const gl = canvas.getContext('webgl2', { preserveDrawingBuffer: true })
	ctx = gl && { canvas, gl, programs: new Map() }
	return ctx
}

/** Links `fragment` into `c.programs` through KHR_parallel_shader_compile, polled on timers, so `drawShaderFrame` finds it linked and never blocks the main thread on the link. */
const link = (c, fragment) => {
	if (c.programs.has(fragment)) return Promise.resolve()
	const { gl } = c
	const ext = gl.getExtension('KHR_parallel_shader_compile')
	const stage = (type, src) => {
		const s = gl.createShader(type)
		gl.shaderSource(s, src)
		gl.compileShader(s)
		return s
	}
	const vs = stage(gl.VERTEX_SHADER, VERTEX)
	const fs = stage(gl.FRAGMENT_SHADER, fragment)
	const prog = gl.createProgram()
	gl.attachShader(prog, vs)
	gl.attachShader(prog, fs)
	gl.linkProgram(prog)
	return new Promise((resolve, reject) => {
		const poll = () => {
			if (gl.isContextLost()) return reject(new Error('thumb context lost'))
			if (ext && !gl.getProgramParameter(prog, ext.COMPLETION_STATUS_KHR)) return setTimeout(poll, 16)
			if (!gl.getProgramParameter(prog, gl.LINK_STATUS))
				return reject(new Error(gl.getShaderInfoLog(fs) || gl.getProgramInfoLog(prog) || 'link failed'))
			c.programs.set(fragment, prog)
			resolve()
		}
		poll()
	})
}

/** A preset's thumb as a Blob, or null when its shader cannot draw. */
export const drawPresetThumb = (source, values) => {
	const run = chain.then(async () => {
		const shader = getShader(source)
		const c = shader && context()
		if (!c) return null
		await link(c, shader.fragment)
		drawShaderFrame(c.gl, {
			vertex: VERTEX,
			fragment: shader.fragment,
			uniforms: thumbUniforms(shader, values),
			size: SIZE,
			time: clockAt(shader, THUMB_TIME),
			programs: c.programs,
		})
		return c.canvas.convertToBlob({ type: THUMBNAIL_MIME, quality: THUMBNAIL_QUALITY })
	})
	chain = run.catch(() => {})
	return run
}

/** Shows `blob` as the preset's thumb for this session. */
export const showLocalThumb = (id, blob) => {
	const old = local.get(id)
	local.set(id, URL.createObjectURL(blob))
	// After React re-renders off it.
	if (old) requestAnimationFrame(() => URL.revokeObjectURL(old))
	version++
	for (const fn of listeners) fn()
}

/** `{ url, cdn }` for a preset's thumb: this session's draw, else the uploaded one (`cdn`: a credentialed load); null when it has none. */
export const presetThumb = preset => {
	const url = local.get(preset.id)
	if (url) return { url, cdn: false }
	return preset.thumbnailUrl ? { url: preset.thumbnailUrl, cdn: true } : null
}

const subscribe = fn => {
	listeners.add(fn)
	return () => listeners.delete(fn)
}

/** Re-renders whenever a thumb is drawn. */
export const usePresetThumbsVersion = () => useSyncExternalStore(subscribe, () => version)
